import { zodResolver } from "@hookform/resolvers/zod";
import { useCallback, useMemo, useState } from "react";
import { useForm } from "react-hook-form";

import { useClientApi, useContentResource, useTracker } from "@ctv/core";
import { DocumentFieldValue, useLocale } from "@ctv/shared-core/src";
import { useDispatchSnackbar } from "@ctv/shared/contexts/SnackbarContext";

import { usePreRegisterConfirm } from "../../_contexts/PreRegisterConfirmContext";
import { useRegisterContent } from "../../_contexts/RegisterContentContext";
import {
  REGISTRATION_API,
  RegistrationRequest,
  RegistrationResponse,
} from "../../apis";
import {
  RegistrationFormValue,
  RegistrationStatus,
  ValidConfirmation,
} from "../../types";
import {
  pageMap,
  SUBMIT_STEP,
  trackingMap,
  transformPreprocessDefaultValues,
} from "./helper";
import { registerSchemaValidation } from "./schema";

export default function useRegistrationWizard() {
  const track = useTracker("ors-page");
  const openSnackbar = useDispatchSnackbar();
  const registrationData = usePreRegisterConfirm();

  const { token } = useRegisterContent();
  const locale = useLocale();
  const { CorporateRegistration: cr } = useContentResource();

  const submitForm = useClientApi<RegistrationResponse, RegistrationRequest>({
    domain: "management",
    method: "post",
    path: REGISTRATION_API,
  });

  const [step, setStep] = useState<number>(
    registrationData?.status === RegistrationStatus.INCOMPLETE_DATA ? 3 : 0
  );

  const defaultValue = useMemo(() => {
    const {
      documents = [],
      agreementDocuments = [],
      ...data
    } = registrationData ?? ({} as ValidConfirmation);

    // Check if there are multiple documents of the same type
    const documentMultipleFlag =
      documents?.reduce((acc: Record<string, boolean>, doc) => {
        acc[doc.documentType] = acc[doc.documentType] !== undefined;
        return acc;
      }, {}) ?? {};

    // transform documents data to be used in the form
    const transformedDocuments = documents?.reduce(
      (acc: Record<string, DocumentFieldValue | DocumentFieldValue[]>, doc) => {
        if (doc.fileNameEncrypted) {
          const documentValue = {
            uploaded: true,
            label: doc.label,
            url: doc.fileNameEncrypted,
            file: new File([], doc.fileName),
          };
          if (documentMultipleFlag[doc.documentType]) {
            if (acc[doc.documentType] === undefined) {
              acc[doc.documentType] = [documentValue];
            } else {
              (acc[doc.documentType] as DocumentFieldValue[]).push(
                documentValue
              );
            }
          } else {
            acc[doc.documentType] = documentValue;
          }
        }
        return acc;
      },
      {}
    );

    // transform agreement documents data to be used in the form
    const transformedAgreementDocuments = agreementDocuments?.reduce(
      (acc: Record<string, DocumentFieldValue>, doc) => {
        acc[doc.documentType] = {
          uploaded: true,
          label: doc.label,
          url: doc.fileNameEncrypted,
          file: new File([], doc.fileName),
        };
        return acc;
      },
      {}
    );

    return transformPreprocessDefaultValues(data, {
      document: transformedDocuments,
      agreementDocument: transformedAgreementDocuments,
    });
  }, [registrationData]);

  const steps = useMemo(
    () => [cr.step1V2, cr.step2V2, cr.step3, cr.step4, cr.step5],
    [cr]
  );

  const customResolver = useCallback(() => {
    if (step === 3) return undefined;
    return zodResolver(registerSchemaValidation(step, locale, cr));
  }, [step, locale]);

  const methods = useForm<RegistrationFormValue>({
    defaultValues: defaultValue,
    resolver: customResolver(),
    mode: "onSubmit",
    reValidateMode: "onChange",
  });

  async function onSubmit(value: RegistrationFormValue) {
    const isSubmit = step === SUBMIT_STEP;

    // Add documents to the request
    const documents: RegistrationRequest["documents"] = [];
    const usedDocs: Record<string, DocumentFieldValue | DocumentFieldValue[]> =
      {
        ...(value.document && Object.keys(value.document).length
          ? value.document
          : defaultValue.document),
      };
    Object.keys(usedDocs).forEach((doc) => {
      if (usedDocs[doc]) {
        const v = (
          !Array.isArray(usedDocs[doc]) ? [usedDocs[doc]] : usedDocs[doc]
        ) as DocumentFieldValue[];

        documents.push(
          ...v.filter(Boolean).map((d) => ({
            documentType: doc,
            fileName: d.url,
          }))
        );
      }
    });

    // Add agreement documents to the request
    const agreementDocuments: RegistrationRequest["agreementDocuments"] = [];
    const usedAgreementDocs: Record<string, DocumentFieldValue> = {
      ...(value.agreementDocument && Object.keys(value.agreementDocument).length
        ? value.agreementDocument
        : defaultValue.agreementDocument),
    };
    Object.keys(usedAgreementDocs).forEach((doc) => {
      if (usedAgreementDocs[doc]) {
        agreementDocuments.push({
          documentType: doc,
          fileName: usedAgreementDocs[doc].url,
        });
      }
    });

    const mergeValueBasedOnStep = { ...defaultValue, ...value };

    const {
      phone,
      companyName,
      officePhone,
      companyIndustry,
      partnerCompanyPhone,
      document: _,
      agreementDocument: __,
      tnc: ___,
      picEmail: ____,
      ...rest
    } = mergeValueBasedOnStep;

    const payload: RegistrationRequest = {
      ...rest,
      token,
      documents,
      agreementDocuments,
      draft: !isSubmit,
      pagePosition: pageMap(step),
      corporateName: companyName,
      corporateIndustry: companyIndustry,
      picPhone: phone.phone,
      picPhoneCountryCode: phone.prefix,
      officePhone: officePhone.phone,
      officePhoneCountryCode: officePhone.prefix,
      partnerCompanyPhone: partnerCompanyPhone.phone,
      partnerCompanyPhoneCountryCode: partnerCompanyPhone.prefix,
    };

    try {
      const response = await submitForm(payload);
      if (response.success) {
        if (!isSubmit) {
          setStep(step + 1);
          window.scrollTo({ top: 0 });
          openSnackbar({ message: cr.submitSuccessMessage, variant: "normal" });

          const eventValue = trackingMap(step);
          if (eventValue) {
            track("corpB2b.onlineRegistration.webEvent", {
              eventName: "CORP_B2B.ONLINE_REGISTRATION.WEB_EVENT",
              eventVersion: "1.0.0",
              pageName: "ORS_PAGE",
              pageCategory: "ORS_PAGE",
              eventValue,
              emailAddress: value.picEmail,
              corporateName: registrationData?.corporateName,
            }).send();
          }
        } else {
          window.location.reload();
        }
      } else {
        throw new Error();
      }
    } catch {
      openSnackbar({ message: cr.submitErrorMessage, variant: "alert" });
    }
  }

  function onClickBack() {
    setStep(step - 1);
    window.scrollTo({ top: 0 });
  }

  function onClickContinue() {
    setStep(step + 1);
    window.scrollTo({ top: 0 });
  }

  return {
    step,
    steps,
    methods,
    onSubmit,
    onClickBack,
    onClickContinue,
  };
}
